import { createContext, useContext, useEffect, useRef } from 'react'

export interface SettingsSaveState {
  dirtyCount: number,
  isSubmitting: boolean,
  canSave: boolean,
  onSave: () => void,
  onDiscard: () => void,
  /** What changed, in words. Default: "N unsaved changes in this section". */
  summary?: string,
  /** A price the change leads to ("$15"), drawn beside the buttons. */
  total?: string,
  /** The unit after the total. Default: "/ month". */
  totalUnit?: string,
  /** Eyebrow over the total. Default: "new total". */
  totalLabel?: string,
  /** The primary button. Default: "Save changes". */
  saveLabel?: string,
  /** The primary button while submitting. Default: "Saving…". */
  savingLabel?: string,
  /**
   * Keep the bar up with nothing changed — the Plan tab, where the bar is
   * where the price lives. The button then reads `idleLabel` and is off.
   */
  alwaysVisible?: boolean,
  /** The disabled button while nothing changed. Default: "No changes yet". */
  idleLabel?: string,
  /** Eyebrow over the total while nothing changed. Default: "total". */
  idleTotalLabel?: string,
  /** A link under the summary while nothing changed. */
  hint?: string,
  onHint?: () => void,
}

/**
 * Each tab's form owns its own dirty state, but the bar that saves it is drawn
 * once, at the bottom of the screen, next to the section picker that has to
 * know whether it is there. So the form publishes; the footer subscribes.
 *
 * The alternative — every form rendering its own fixed bar — is what this
 * replaces, and it could not stack: two fixed elements at the same offset with
 * no knowledge of each other just overlap.
 */
const SettingsSaveContext = createContext<(state: SettingsSaveState | null) => void>(() => {});

export const SettingsSaveProvider = SettingsSaveContext.Provider;

/**
 * Publishes this form's save state for as long as it is mounted, and withdraws
 * it on the way out so a tab with nothing to save shows no bar.
 *
 * The handlers go through a ref: they close over render-scope state and change
 * identity every render, and putting them in the dependency array would
 * republish on every keystroke.
 */
export const useSettingsSave = ({
  dirtyCount,
  isSubmitting = false,
  canSave = true,
  onSave,
  onDiscard,
  summary,
  total,
  totalUnit,
  totalLabel,
  saveLabel,
  savingLabel,
  alwaysVisible,
  idleLabel,
  idleTotalLabel,
  hint,
  onHint,
}: {
  dirtyCount: number,
  isSubmitting?: boolean,
  canSave?: boolean,
  onSave: () => void,
  onDiscard: () => void,
  summary?: string,
  total?: string,
  totalUnit?: string,
  totalLabel?: string,
  saveLabel?: string,
  savingLabel?: string,
  alwaysVisible?: boolean,
  idleLabel?: string,
  idleTotalLabel?: string,
  hint?: string,
  onHint?: () => void,
}) => {
  const publish = useContext(SettingsSaveContext);
  const handlers = useRef({ onSave, onDiscard, onHint });
  handlers.current = { onSave, onDiscard, onHint };

  useEffect(() => {
    publish({
      dirtyCount,
      isSubmitting,
      canSave,
      onSave: () => handlers.current.onSave(),
      onDiscard: () => handlers.current.onDiscard(),
      summary,
      total,
      totalUnit,
      totalLabel,
      saveLabel,
      savingLabel,
      alwaysVisible,
      idleLabel,
      idleTotalLabel,
      hint,
      onHint: onHint ? () => handlers.current.onHint?.() : undefined,
    });
    return () => publish(null);
  }, [publish, dirtyCount, isSubmitting, canSave, summary, total, totalUnit, totalLabel, saveLabel, savingLabel, alwaysVisible, idleLabel, idleTotalLabel, hint, !!onHint]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default SettingsSaveContext
